<template>
  <template v-if="!isPriceBox">
    <!-- CALIBRATED ITEM INPUTS -->
    <div v-if="isCalibrated" class="add-to-cart__calibrated grid auto-cols-[max-content_auto] gap-y-5 print:hidden">
      <VcInput
        v-model="itemNumber"
        :label="$t('shared.product.calibrated_item.item_number_input_label')"
        :placeholder="$t('shared.product.calibrated_item.item_number_input_placeholder')"
        :message="itemNumberErrors"
        :error="!!itemNumberErrors"
        name="itemNumber"
        input-class="grid grid-cols-[subgrid] col-span-2 w-full items-center"
      />
      <VcInput
        v-model="serialNumber"
        :label="$t('shared.product.calibrated_item.serial_number_input_label')"
        :placeholder="$t('shared.product.calibrated_item.serial_number_input_placeholder')"
        :message="serialNumberErrors"
        :error="!!serialNumberErrors"
        name="serialNumber"
        input-class="grid grid-cols-[subgrid] col-span-2 items-center"
      />
    </div>

    <!-- CUSTOM LENGTH INPUT -->
    <div v-if="customLengthSection" class="grid gap-y-8 print:hidden">
      <div class="add-to-cart__calibrated grid grid-cols-1 gap-y-1">
        <VcInput
          v-model="customLength"
          :label="$t('pages.configurator.optical_rails.custom_length')"
          type="number"
          step="0.01"
          :placeholder="$t('pages.configurator.optical_rails.custom_length')"
          allow-decimals
          :aria-label="$t('pages.configurator.optical_rails.custom_length')"
          size="lg"
          :min="minLength"
          :max="maxLength"
          :message="customLengthErrors"
          :error="!!customLengthErrors"
          name="itemNumber"
          input-class=" col-span-2 w-full"
          @keypress="onLengthKeypress"
          @blur="onLengthBlur"
        />
        <VcLabel :error="!!customLengthErrors" class="pt-0 text-sm text-[--color-neutral-a2]">
          {{ lengthLabel }}</VcLabel
        >
      </div>

      <div v-if="customLengthSection && configuredLineItemPrice" class="text-[22px] leading-none">
        <VcItemPrice
          v-if="configuredLineItemPrice"
          :value="{
            list: configuredLineItemPrice.listPrice!,
            actual: configuredLineItemPrice.salePrice,
          }"
          :list-with-tax="configuredLineItemPrice.listPriceWithTax"
          :actual-with-tax="configuredLineItemPrice.salePrice"
          show-no-discounts
        >
        </VcItemPrice>
      </div>
      <div v-else>
        <span class="font-medium italic leading-[27px]">
          <span class="text-[22px] font-bold not-italic leading-[27px]">{{ currentCurrency.symbol }} -</span>
          {{ $t("pages.configurator.price_depends_on_length") }}</span
        >
      </div>
    </div>

    <!-- REST OF THE ORIGINAL PRICING LOGIC -->
    <div class="relative flex gap-6 print:hidden">
      <template v-if="props.isConfigurable && !customLengthSection">
        <VcButton
          color="primary"
          size="lg"
          variant="solid"
          truncate
          class="add-to-cart__button flex-1 !py-[11px] !font-bold"
          @click="onChange"
        >
          {{ t("pages.manuals_portal.view_product_family") }}
        </VcButton>
      </template>
      <template v-else>
        <VcInput
          v-model="enteredQuantity"
          type="number"
          :aria-label="$t('common.labels.product_quantity')"
          :disabled="qtyDisabled"
          :max="maxQty"
          :min="minQty"
          :error="!!errorMessage"
          :message="errorMessage"
          single-line-message
          center
          show-empty-details
          select-on-click
          size="lg"
          :label="$t('Qty:')"
          class="add-to-cart"
          :class="{ hidden: hideQuantity }"
          @input="onInput"
          @keypress="onKeypress"
          @blur="onBlur"
        >
        </VcInput>
        <VcButton
          color="primary"
          size="lg"
          variant="solid"
          :loading="loading"
          :disabled="disabled || !!errorMessage"
          truncate
          class="add-to-cart__button flex-1 !py-[11px] !font-bold"
          @click="onChange"
        >
          <template v-if="buttonText === t('common.buttons.added')" #prepend>
            <VcIcon name="check-circle-alt" :size="20" class="mr-2" />
          </template>
          {{ buttonText }}
        </VcButton>
      </template>
    </div>
    <!-- PER ITEM NOTE SECTION -->
    <div v-if="perItemNote" class="per-item-note">
      <p v-dompurify-html="perItemNote"></p>
    </div>
  </template>
  <template v-if="isPriceBox">
    <TlLoadingButton
      color="custom"
      custom-color="var(--color-neutral-a1)"
      custom-hover-color="var(--color-neutral-a0)"
      variant="outlineTL"
      :aria-label="$t('common.buttons.add_to_cart')"
      :complete-text="$t(``)"
      :complete-with-error="completedWithErrors"
      :disabled="externalDisabled || (customLengthSection && !configuredLineItemPrice)"
      class="add-to-cart__btn-small !px-2 !py-[7px]"
      @click="onChangeWrapper"
    >
      <VcIcon name="plus" :size="20" class="icon-plus" />
      <VcIcon name="icon-cart-shopping-md" :size="20" />
    </TlLoadingButton>
  </template>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/yup";
import { clone } from "lodash";
import { useField } from "vee-validate";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { getConfiguredLineItem } from "@/core/api/graphql/cart/queries/getConfiguredLineItem";
import { useCurrency, useErrorsTranslator, useGoogleAnalytics } from "@/core/composables";
import { LINE_ITEM_QUANTITY_LIMIT } from "@/core/constants";
import { ValidationErrorObjectType } from "@/core/enums";
import { Logger } from "@/core/utilities";
import { useShortCart } from "@/shared/cart/composables";
import { useNotifications } from "@/shared/notification";
import { eventBus } from "@/shared/thorlabs/shared/event-bus";
import { useQuantityValidationSchema } from "@/ui-kit/composables";
import type {
  ConfigurableProductOptionInput,
  ConfigurationLineItemTypeExtension,
  ConfigurationSectionInputTl,
  ConfigurationSectionType,
  Product,
  ShortLineItemFragment,
  VariationTypeExtension,
} from "@/core/api/graphql/types";
import type { NamedValue } from "vue-i18n";
import TlLoadingButton from "@/ui-kit/components/molecules/button/tl-loading-button.vue";

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const notifications = useNotifications();
const { currentCurrency } = useCurrency();

const itemNumber = props.isExternalCalibration
  ? computed(() => (props.externalItemNumber ? props.externalItemNumber : ""))
  : ref("");
const itemNumberErrors = ref("");
const serialNumber = props.isExternalCalibration
  ? computed(() => (props.externalSerialNumber ? props.externalSerialNumber : ""))
  : ref("");
const serialNumberErrors = ref("");

const customLength = ref<number>();
const customLengthErrors = ref("");
const configuredLineItemPrice = ref<ConfigurationLineItemTypeExtension>();

const completedWithErrors = computed(
  () => itemNumberErrors.value != "" || serialNumberErrors.value != "" || customLengthErrors.value != "",
);
const router = useRouter();

interface IEmits {
  (event: "update:lineItem", lineItem: ShortLineItemFragment): void;
  (event: "update:enteredQuantity", enteredQuantity: number): void;
  (event: "update:enteredLength", enteredLength: number): void;
  (event: "update:itemNumberErrors", payload: { code: string; error: string }): void;
  (event: "update:serialNumberErrors", payload: { code: string; error: string }): void;
  (event: "update:customLengthErrors", error: string): void;
  (event: "update:customLengthPrice", price: ConfigurationLineItemTypeExtension | undefined): void;
}

interface IProps {
  product: Product | VariationTypeExtension;
  reservedSpace?: boolean;
  hasVariations?: boolean;
  isCalibrated?: boolean;
  isConfigurable?: boolean;
  hideQuantity?: boolean;
  isPriceBox?: boolean;
  priceBoxQuantity?: number;
  priceBoxCustomLength?: number;
  externalSerialNumber?: string;
  externalItemNumber?: string;
  externalDisabled?: boolean;
  isExternalCalibration?: boolean;
  configurationSectionTypes?: ConfigurationSectionType[];
}

const isInStock = computed(
  () => props.product.availabilityData?.isInStock && props.product.availabilityData?.isBuyable,
);
const availableQuantity = computed(() => props.product.availabilityData?.availableQuantity);
const minQuantity = computed(() => props.product.minQuantity);
const maxQuantity = computed(() => props.product.maxQuantity);
const bypassMaxQuantity = ref(true);

const { cart, addToCart } = useShortCart();
const { t } = useI18n();
const ga = useGoogleAnalytics();
const { getTranslation } = useErrorsTranslator("validation_error");
const { quantitySchema } = useQuantityValidationSchema({
  isInStock,
  availableQuantity,
  minQuantity,
  maxQuantity,
  bypassMaxQuantity,
});

const loading = ref(false);
const variationAdded = ref(false);
const addedToCart = ref(false);

const countInCart = computed<number>(() => getLineItem(cart.value?.items)?.quantity || 0);
const minQty = computed<number>(() => minQuantity.value || 1);
const maxQty = computed<number>(() => LINE_ITEM_QUANTITY_LIMIT);

const disabled = computed<boolean>(
  () =>
    ((loading.value || !props.product.availabilityData?.isAvailable) && !customLengthSection.value) ||
    (!!customLengthSection.value && !configuredLineItemPrice.value),
);
const qtyDisabled = computed<boolean>(() => (disabled.value || props.isCalibrated) && !customLengthSection.value);

const buttonText = computed<string>(() =>
  addedToCart.value ? t("common.buttons.added") : t("common.buttons.add_to_cart"),
);

const rules = computed(() => toTypedSchema(quantitySchema.value));

const perItemNote = computed(() => getCurrencySpecificItemNote());

function getCurrencySpecificItemNote(): string | undefined {
  const currencyCode = currentCurrency.value.code.toLowerCase();
  const targetReviewType = currencyCode === "usd" ? "peritemnote" : `peritemnote${currencyCode}`;
  let noteText = undefined;
  for (const description of props.product?.descriptionsWithFallbacksTL ?? []) {
    if (description.reviewType?.toLowerCase() === targetReviewType && description.content) {
      noteText = description.content;
    }
  }
  return noteText;
}

const customLengthSection = computed(() => {
  return props.configurationSectionTypes?.filter((c) => c.name === "Length")[0];
});

const customLengthProduct = computed(() => {
  return customLengthSection.value?.options?.[0]?.product ?? null;
});

const minLength = computed(() => {
  return customLengthProduct.value?.properties?.filter((p) => p.name === "Minimum")[0]?.value as number;
});

const maxLength = computed(() => {
  return customLengthProduct.value?.properties?.filter((p) => p.name === "Maximum")[0]?.value as number;
});

const lengthUnit = computed(() => {
  return props.product.code === "XE25-CUSTRAIL" ? t("common.units.inches") : t("common.units.mm");
});

const lengthLabel = computed(() => {
  return t("pages.configurator.optical_rails.min_max_description", [
    minLength.value,
    maxLength.value,
    lengthUnit.value,
  ]);
});

const customDiscountSection = computed(() => {
  return props.configurationSectionTypes?.filter((c) => c.name === "Discount")[0] ?? null;
});

const customDiscountProduct = computed(() => {
  return customDiscountSection.value?.options?.[0]?.product ?? null;
});

function getSerialNumberToAdd() {
  return props.isCalibrated ? serialNumber.value : props.product.mainProductId ? props.product.code : "";
}

const enteredQuantity = ref(!disabled.value ? minQty.value : undefined);
const inputQuantity = computed(() =>
  props.isPriceBox && props.priceBoxQuantity ? props.priceBoxQuantity : enteredQuantity.value || minQty.value,
);

const inputCustomLength = computed(() =>
  props.isPriceBox && props.priceBoxCustomLength ? props.priceBoxCustomLength : customLength.value || 0,
);
watch(customLengthSection, (newValue) => {
  if (newValue && !enteredQuantity.value) {
    enteredQuantity.value = minQty.value;
  }
});

const { errorMessage, validate, setValue } = useField("quantity", rules, {
  initialValue: enteredQuantity,
  validateOnMount: true,
});

/**
 * Process button click to add/update cart line item.
 */
async function onChange(callback: () => void) {
  if (props.isConfigurable && !customLengthSection.value) {
    const familyPageUrl = props.product.descriptionsWithFallbacksTL?.find(
      (x) => x.reviewType === "FamilyPageUrl",
    )?.content;
    await router.push({ path: familyPageUrl });
  }

  if (addedToCart.value) {
    return;
  }

  if (props.isCalibrated) {
    const calibrationFormValid = validateFormOnCalibrated();

    if (!calibrationFormValid) {
      if (props.isExternalCalibration) {
        callback();
      }
      return;
    }
  }

  if (customLengthSection.value && !validateLength()) {
    return;
  }

  const { valid } = await validate();

  if (!valid || disabled.value) {
    if (props.isExternalCalibration) {
      callback();
    }
    return;
  }

  addedToCart.value = false;
  loading.value = true;

  let lineItem = getLineItem(cart.value?.items);

  const isAlreadyExistsInTheCart = !!lineItem;

  // if (isAlreadyExistsInTheCart) {
  //   const quantityToAdd = enteredQuantity.value || 0;
  //   updatedCart = await changeItemQuantity(
  //     lineItem!.id,
  //     lineItem?.quantity ? lineItem?.quantity + quantityToAdd : quantityToAdd,
  //   );
  // } else {
  //   const inputQuantity = enteredQuantity.value || minQty.value;

  //   updatedCart = await addToCart(props.product.id!, inputQuantity, props.product.serialNumberTL);

  //   /**
  //    * Send Google Analytics event for an item added to cart.
  //    */
  //   ga.addItemToCart(props.product, inputQuantity);
  // }

  const updatedCart = await addToCart(
    props.product.id!,
    inputQuantity.value,
    getSerialNumberToAdd(),
    itemNumber.value,
    computeCustomDescription(),
    computeConfigurationSection(),
  );

  ga.addItemToCart(props.product, inputQuantity.value);

  lineItem = clone(getLineItem(updatedCart?.items));

  if (!lineItem) {
    Logger.error(onChange.name, 'The variable "lineItem" must be defined');
    notifications.error({
      text: t(
        isAlreadyExistsInTheCart
          ? "common.messages.fail_to_change_quantity_in_cart"
          : "common.messages.fail_add_product_to_cart",
        {
          reason: updatedCart?.validationErrors
            ?.filter(
              (validationError) =>
                validationError.objectId === props.product.id &&
                validationError.objectType === ValidationErrorObjectType.CatalogProduct,
            )
            .map((el) => {
              return getTranslation({
                code: el.errorCode,
                parameters: el.errorParameters?.reduce((acc, err) => {
                  acc[err.key] = err.value;
                  return acc;
                }, {} as NamedValue),
                description: el.errorMessage,
              });
            })
            .join(" "),
        },
      ),
      duration: 4000,
      single: true,
    });
  } else {
    emit("update:lineItem", lineItem);

    // Emit event for cart notification components
    eventBus.emit("cartItemAdded", { productCode: props.product.code || "", quantity: inputQuantity.value });
  }

  loading.value = false;
  addedToCart.value = true;

  if (props.hasVariations) {
    variationAdded.value = true;
  }

  setTimeout(() => {
    addedToCart.value = false;
  }, 2000);

  callback();
}

async function createConfigurableLineItem() {
  if (!inputQuantity.value || !validateLength()) {
    return;
  }

  configuredLineItemPrice.value = await getConfiguredLineItem(
    props.product!.id,
    inputQuantity.value,
    computeConfigurationSection()!,
  );
  emit("update:customLengthPrice", configuredLineItemPrice.value);
}

function computeCustomDescription(): string | undefined {
  if (!customLengthSection.value) {
    return undefined;
  }

  const title = props.product.descriptionsWithFallbacksTL?.filter((d) => d.reviewType === "Title")[0]?.content;

  // Remove HTML tags from title if present
  const cleanTitle = title ? title.replace(/<[^>]*>/g, "") : "";
  return `${cleanTitle}, L = ${inputCustomLength.value}${props.product.code === "XE25-CUSTRAIL" ? '"' : " " + t("common.units.mm")}`;
}

function computeConfigurationSection(): ConfigurationSectionInputTl[] | undefined {
  if (!customLengthSection.value) {
    return undefined;
  }

  return [
    {
      sectionId: customDiscountSection.value?.id,
      type: "Product",
      value: {
        productId: customDiscountProduct.value?.id,
        quantity: 1,
      } as ConfigurableProductOptionInput,
    } as ConfigurationSectionInputTl,
    {
      sectionId: customLengthSection.value?.id,
      type: "Product",
      customText: inputCustomLength.value?.toString(),
      value: {
        productId: customLengthProduct.value?.id,
        quantity: 1,
      } as ConfigurableProductOptionInput,
    } as ConfigurationSectionInputTl,
  ];
}

const onChangeWrapper = (callback: (result: unknown) => void): void => {
  const adaptedCallback = () => {
    callback(undefined); // Call the original callback with an undefined result
  };
  onChange(adaptedCallback);
};

function getLineItem(items?: ShortLineItemFragment[]): ShortLineItemFragment | undefined {
  return items?.find((item) => item.productId === props.product.id);
}

/**
 * Ignore non-numeric keys.
 */
function onKeypress(event: KeyboardEvent) {
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
}

function onLengthKeypress(event: KeyboardEvent) {
  if (!/[0-9]|./.test(event.key)) {
    event.preventDefault();
  }
}

/**
 * Limit max value.
 */
function onInput() {
  if (!enteredQuantity.value) {
    enteredQuantity.value = undefined;
  } else if (enteredQuantity.value > LINE_ITEM_QUANTITY_LIMIT) {
    enteredQuantity.value = LINE_ITEM_QUANTITY_LIMIT;
  } else {
    setValue(enteredQuantity.value);
  }
}

async function onBlur() {
  if (!enteredQuantity.value || enteredQuantity.value < 1) {
    enteredQuantity.value = countInCart.value || minQty.value;
  }
  await createConfigurableLineItem();
  emit("update:enteredQuantity", enteredQuantity.value);
}

async function onLengthBlur() {
  await createConfigurableLineItem();
  if (inputCustomLength.value) {
    emit("update:enteredLength", inputCustomLength.value);
  }
}

watch(
  () => props.priceBoxQuantity,
  async () => {
    await createConfigurableLineItem();
  },
);

watch(
  () => props.priceBoxCustomLength,
  async () => {
    await createConfigurableLineItem();
  },
);

function validateFormOnCalibrated() {
  itemNumberErrors.value = "";
  serialNumberErrors.value = "";

  let isValid = true;

  if (
    !props.isCalibrated ||
    (props.isCalibrated &&
      props.externalItemNumber &&
      props.externalSerialNumber &&
      props.externalItemNumber.trim() !== "" &&
      props.externalSerialNumber?.trim() !== "")
  ) {
    return isValid;
  }

  if (!itemNumber.value || itemNumber.value.trim() === "") {
    const errorMsg = t("shared.product.calibrated_item.input_error_label");
    itemNumberErrors.value = errorMsg;
    emit("update:itemNumberErrors", { code: props.product.code, error: errorMsg });
    isValid = false;
  } else {
    emit("update:itemNumberErrors", { code: props.product.code, error: "" });
  }

  if (!serialNumber.value || serialNumber.value.trim() === "") {
    const errorMsg = t("shared.product.calibrated_item.input_error_label");
    serialNumberErrors.value = errorMsg;
    emit("update:serialNumberErrors", { code: props.product.code, error: errorMsg });
    isValid = false;
  } else {
    emit("update:serialNumberErrors", { code: props.product.code, error: "" });
  }
  return isValid;
}

function validateLength() {
  customLengthErrors.value = "";

  if (!inputCustomLength.value) {
    const errorMsg = t("shared.product.calibrated_item.input_error_label");
    customLengthErrors.value = errorMsg;
    configuredLineItemPrice.value = undefined;
    emit("update:customLengthErrors", errorMsg);
    emit("update:customLengthPrice", undefined);
    return false;
  } else {
    emit("update:customLengthErrors", "");
    emit("update:customLengthPrice", configuredLineItemPrice.value);
  }

  if (inputCustomLength.value < minLength.value || inputCustomLength.value > maxLength.value) {
    const errorMsg = t("pages.configurator.optical_rails.min_max_validation_message", [
      minLength.value,
      maxLength.value,
    ]);
    customLengthErrors.value = errorMsg;
    configuredLineItemPrice.value = undefined;
    emit("update:customLengthErrors", errorMsg);
    emit("update:customLengthPrice", undefined);
    return false;
  } else {
    emit("update:customLengthErrors", "");
    emit("update:customLengthPrice", configuredLineItemPrice.value);
  }

  return true;
}
</script>

<style lang="scss">
.add-to-cart {
  .vc-line-item__slot:has(&, * &) {
    @apply w-[13rem];

    @container (width > theme("containers.2xl")) {
      @apply w-[15.7rem];
    }
  }

  &.vc-input {
    @apply flex-row gap-3 items-center;
  }

  .vc-input {
    &__input {
      @apply max-w-16 px-1 min-w-16;
    }
  }

  .vc-input-details {
    @apply absolute top-[calc(100%+5px)] left-0;
  }

  &__button {
    --vc-button-min-width: 160px;
  }

  &__btn-small {
    --vc-button-min-width: 70px !important;
    width: var(--vc-button-min-width);

    .vc-icon {
      &:first-child:not(:last-child) {
        @apply me-0;
      }

      &:last-child:not(:first-child) {
        @apply ms-1;
      }
    }
  }
}

.per-item-note {
  a {
    @apply text-[--color-link] hover:text-[--color-neutral-a1];
  }
}
</style>
